/**
 * TableView — Universal reusable table for the Three-View Discipline (7W columns).
 *
 * Mobile (<768px): renders card list via mobileCardRenderer.
 * Desktop (≥768px): sortable table with column headers.
 * Always: filter chips + search box at top, pagination at bottom.
 *
 * See: docs/architecture/BUSINESS_EVENT_MODEL.md (7W model)
 * See: docs/architecture/UX_NAVIGATION_PATTERNS_V2.md §9 (Three-View Discipline)
 */
import { useState, useMemo, useCallback, useEffect } from 'react';

const PAGE_SIZE = 20;

function useIsNarrow() {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768
  );
  useEffect(() => {
    const h = () => setNarrow(window.innerWidth < 768);
    window.addEventListener('resize', h, { passive: true });
    return () => window.removeEventListener('resize', h);
  }, []);
  return narrow;
}

export default function TableView({
  columns = [],
  data = [],
  searchKeys = [],
  defaultSort = null,
  // filterDefs: [{ key, options: [{ value, label, filterFn? }] }]
  // filterFn(row): boolean — custom row predicate; if absent, uses row[key] === value
  filterDefs = [],
  emptyState = null,
  onRowClick = null,
  mobileCardRenderer = null,
  loading = false,
}) {
  const isNarrow = useIsNarrow();
  const [search, setSearch] = useState('');
  const [activeFilters, setActiveFilters] = useState({});
  const [sort, setSort] = useState(defaultSort);
  const [page, setPage] = useState(0);

  // Reset to page 0 when filters/search/sort change
  useEffect(() => { setPage(0); }, [search, activeFilters, sort]);

  // Apply text search + filter chips
  const filtered = useMemo(() => {
    let rows = data;

    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(row =>
        searchKeys.some(k => String(row[k] ?? '').toLowerCase().includes(q))
      );
    }

    Object.entries(activeFilters).forEach(([key, val]) => {
      if (!val || val === '__all__') return;
      const def = filterDefs.find(f => f.key === key);
      const opt = def?.options.find(o => o.value === val);
      if (opt?.filterFn) {
        rows = rows.filter(opt.filterFn);
      } else {
        rows = rows.filter(row => String(row[key] ?? '') === val);
      }
    });

    return rows;
  }, [data, search, activeFilters, searchKeys, filterDefs]);

  // Sort
  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const { key, direction } = sort;
    return [...filtered].sort((a, b) => {
      const av = a[key] ?? '';
      const bv = b[key] ?? '';
      const cmp = String(av).localeCompare(String(bv), 'de', { numeric: true });
      return direction === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sort]);

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const pageData = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const handleSort = useCallback((col) => {
    if (!col.sortable) return;
    setSort(prev =>
      prev?.key === col.key
        ? { key: col.key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key: col.key, direction: 'asc' }
    );
  }, []);

  const handleFilterChange = useCallback((key, val) => {
    setActiveFilters(prev => ({ ...prev, [key]: val }));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="table-view-loading">
        <div className="w-8 h-8 border-4 border-[var(--aif-border-default)] border-t-[var(--aif-color-primary)] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="table-view">
      {/* Search */}
      <input
        type="text"
        placeholder="Suchen…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full px-3 py-2 min-h-[44px] rounded-xl bg-[var(--aif-bg-elevated)]
                   border border-[var(--aif-border-default)] text-[var(--aif-text-primary)]
                   placeholder-[var(--aif-text-muted)] focus:outline-none focus:border-[var(--aif-color-primary)]
                   text-sm"
        data-testid="table-view-search"
      />

      {/* Filter chip rows — one row per filterDef */}
      {filterDefs.map(fd => (
        <div key={fd.key} className="flex gap-2 overflow-x-auto pb-0.5 flex-nowrap" data-testid={`filter-row-${fd.key}`}>
          {fd.options.map(opt => {
            const active = (activeFilters[fd.key] ?? '__all__') === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => handleFilterChange(fd.key, opt.value)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition min-h-[32px]
                  ${active
                    ? 'bg-[var(--aif-color-primary)] text-white'
                    : 'bg-[var(--aif-bg-elevated)] text-[var(--aif-text-secondary)] hover:bg-[var(--aif-bg-surface)]'
                  }`}
                data-testid={`filter-chip-${fd.key}-${opt.value}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      ))}

      {/* Empty state */}
      {sorted.length === 0 && emptyState && (
        <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="table-view-empty">
          <div className="text-3xl mb-3">📭</div>
          <p className="text-sm font-medium text-[var(--aif-text-primary)]">{emptyState.title}</p>
          {emptyState.subtitle && (
            <p className="text-xs text-[var(--aif-text-muted)] mt-1 max-w-xs">{emptyState.subtitle}</p>
          )}
          {emptyState.cta && (
            <button
              onClick={emptyState.cta.onClick}
              className="mt-4 px-4 py-2 rounded-xl bg-[var(--aif-color-primary)] text-white text-sm font-medium
                         hover:opacity-90 transition min-h-[44px]"
              data-testid="table-view-empty-cta"
            >
              {emptyState.cta.label}
            </button>
          )}
        </div>
      )}

      {/* Mobile: stacked cards */}
      {sorted.length > 0 && isNarrow && mobileCardRenderer && (
        <div className="space-y-2" data-testid="table-view-cards">
          {pageData.map((row, i) => (
            <div
              key={row.id ?? i}
              onClick={() => onRowClick?.(row)}
              className={onRowClick ? 'cursor-pointer' : ''}
            >
              {mobileCardRenderer(row)}
            </div>
          ))}
        </div>
      )}

      {/* Desktop: table */}
      {sorted.length > 0 && (!isNarrow || !mobileCardRenderer) && (
        <div className="overflow-x-auto rounded-xl border border-[var(--aif-border-default)]" data-testid="table-view-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--aif-border-default)] bg-[var(--aif-bg-elevated)]">
                {columns.map(col => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col)}
                    className={`px-3 py-2.5 text-left text-xs font-medium text-[var(--aif-text-muted)] whitespace-nowrap select-none
                      ${col.sortable ? 'cursor-pointer hover:text-[var(--aif-text-primary)]' : ''}`}
                    data-testid={`col-header-${col.key}`}
                  >
                    {col.label}
                    {col.sortable && sort?.key === col.key && (
                      <span className="ml-1">{sort.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageData.map((row, i) => (
                <tr
                  key={row.id ?? i}
                  onClick={() => onRowClick?.(row)}
                  className={`border-b border-[var(--aif-border-default)] last:border-0
                    hover:bg-[var(--aif-bg-elevated)] transition-colors
                    ${onRowClick ? 'cursor-pointer' : ''}`}
                  data-testid={`table-row-${row.id ?? i}`}
                >
                  {columns.map(col => (
                    <td key={col.key} className="px-3 py-2.5 text-[var(--aif-text-primary)]">
                      {col.render ? col.render(row) : (row[col.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-1" data-testid="table-view-pagination">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="px-3 py-1.5 rounded-lg text-xs text-[var(--aif-text-secondary)]
                       hover:bg-[var(--aif-bg-elevated)] disabled:opacity-40 transition min-h-[36px]"
            data-testid="pagination-prev"
          >
            ← Zurück
          </button>
          <span className="text-xs text-[var(--aif-text-muted)]" data-testid="pagination-info">
            {page + 1} / {totalPages} · {sorted.length} Einträge
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="px-3 py-1.5 rounded-lg text-xs text-[var(--aif-text-secondary)]
                       hover:bg-[var(--aif-bg-elevated)] disabled:opacity-40 transition min-h-[36px]"
            data-testid="pagination-next"
          >
            Weiter →
          </button>
        </div>
      )}
    </div>
  );
}
